import {
    Typography,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    Grid,
    IconButton,
    TextField,
    DialogTitle,
    Card,
    Box,
    InputAdornment,
    FormHelperText,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { DropzoneContainer } from '../../../../../createProperty/styles';
import { DatePicker } from '../../../../../datePicker/datepicker';
import { InputFieldMoney } from '../../../../../formComponents/inputfieldmoney';
import PublishIcon from '@material-ui/icons/Publish';
import DeleteIcon from '@material-ui/icons/Delete';
import { RootStoreContext } from '../../../../../../global/storeContext';
import StringUtils from '../../../../../../utils/string';

const DAMAGE_SERVICE = 'Damage, missing items and unexpected cleaning';

interface IRequestMoney {
    service: string;
    setService: (value: string) => void;
    amount: number;
    setAmount: (value: number) => void;
    paymentDate: Date;
    setPaymentDate: (date: Date) => void;
    attachments: File[];
    setAttachments: (file: File[]) => void;
    setRequestNote: (value: string) => void;
    requestNote: string;
    files: (File & {
        preview: string;
    })[];
    setFiles: (
        value: (File & {
            preview: string;
        })[],
    ) => void;
    totalBillAmount: number;
    setTotalBillAmount: (value: number) => void;
    percentageOwing: number;
    setPercentageOwing: (value: number) => void;
    dateReceived: Date;
    setDateReceived: (date: Date) => void;
    computedAmount: number;
    showErrors: boolean;
}

export const RequestMoney: React.FC<IRequestMoney> = observer(
    ({
        service,
        setService,
        amount,
        setAmount,
        paymentDate,
        setPaymentDate,
        attachments,
        setAttachments,
        setRequestNote,
        requestNote,
        files,
        setFiles,
        totalBillAmount,
        setTotalBillAmount,
        percentageOwing,
        setPercentageOwing,
        dateReceived,
        setDateReceived,
        computedAmount,
        showErrors,
    }) => {
        const { uiStore } = React.useContext(RootStoreContext);
        const isDamageFlow = service === DAMAGE_SERVICE;

        const generatePreview = (files: File[]) => {
            return files.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                }),
            );
        };

        const onDrop = (acceptedFiles: File[]) => {
            const validateFiles = acceptedFiles.some((f) => {
                const file = f;
                const fileSize = Math.round(file.size / 1024);
                const maxFileSize = 3072;
                return fileSize > maxFileSize;
            });

            if (validateFiles) {
                const errorMsg = 'Invalid file. The file size must not be more than 3 mb';
                console.error(errorMsg);
                uiStore.error(errorMsg);
                return;
            }

            var newFiles = [...attachments, ...acceptedFiles];
            files?.forEach((file: any) => URL.revokeObjectURL(file.preview));
            setFiles([...generatePreview(newFiles)]);
            setAttachments([...newFiles]);
        };

        const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
            useDropzone({
                accept: {
                    imageAndPdf: ['image/*', '.pdf'],
                },
                onDrop,
            });

        const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setService((event.target as HTMLInputElement).value);
            // Reset amount fields when switching service types
            setAmount(0);
            setTotalBillAmount(0);
            setPercentageOwing(100);
        };

        const handleRequestNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setRequestNote(event.target.value);
        };

        const handleAmountInputChange = (value: string | number | null) => {
            const updatedAmount = Number(value);
            if (isNaN(updatedAmount) || updatedAmount < 0) return;
            setAmount(updatedAmount);
        };

        const handleTotalBillAmountChange = (value: string | number | null) => {
            const updated = Number(value);
            if (isNaN(updated) || updated < 0) return;
            setTotalBillAmount(updated);
        };

        const handlePercentageOwingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const val = Number(event.target.value);
            if (isNaN(val) || val < 0 || val > 100) return;
            setPercentageOwing(val);
        };

        const handlePaymentDate = (value: Date | null) => {
            setPaymentDate(value ?? new Date(Date.now()));
        };

        const handleDateReceived = (value: Date | null) => {
            setDateReceived(value ?? new Date());
        };

        const removePhoto = (file: File) => {
            const photoIndex = files.findIndex((f) => f.name === file.name) as number;
            if (photoIndex >= 0) {
                var tempFile = [...attachments];
                tempFile.splice(photoIndex, 1);
                setFiles([...generatePreview(tempFile)]);
                setAttachments([...tempFile]);
            }
        };

        const imageThumbs = files?.map((file: any) => (
            <Grid item xs={12} md={6}>
                <Card key={file.name} style={{ padding: '5px' }}>
                    <Box display='flex' justifyContent='space-between'>
                        <Typography>{StringUtils.truncateString(file.name)}</Typography>
                        <IconButton
                            aria-label='delete'
                            color='secondary'
                            onClick={() => {
                                removePhoto(file);
                            }}
                        >
                            <DeleteIcon fontSize='small' />
                        </IconButton>
                    </Box>
                </Card>
            </Grid>
        ));

        return (
            <>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <DialogTitle style={{ textAlign: 'center' }}>Request Money</DialogTitle>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid>
                            <Typography variant='subtitle1'>
                                <strong>What is this request for?</strong>
                            </Typography>
                        </Grid>
                        <Grid>
                            <FormControl component='fieldset'>
                                <RadioGroup
                                    aria-label='gender'
                                    name='payment'
                                    value={service}
                                    onChange={handleRadioChange}
                                >
                                    <FormControlLabel
                                        value={DAMAGE_SERVICE}
                                        control={<Radio color='primary' />}
                                        label='Damage, missing items and unexpected cleaning'
                                    />
                                    <FormControlLabel
                                        value='Extra Services'
                                        control={<Radio color='primary' />}
                                        label={
                                            <Typography>
                                                Extra services <br />{' '}
                                                <Typography
                                                    variant='body2'
                                                    style={{ marginTop: '2px', opacity: '0.5' }}
                                                >
                                                    Expenses that were not included in the bills
                                                    already created(e.g amenities not included in
                                                    the listing description,transportation
                                                    costs,e.t.c)
                                                </Typography>
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {isDamageFlow ? (
                        <>
                            {/* Damage flow: direct amount entry */}
                            <Grid item xs={12}>
                                <Grid>
                                    <Typography>
                                        <strong>Amount Requested</strong>
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <InputFieldMoney
                                        id='damage-amount'
                                        placeholder='Amount (CAD)'
                                        fullWidth
                                        value={amount}
                                        onChange={handleAmountInputChange}
                                        error={showErrors && !amount}
                                        helperText={showErrors && !amount ? 'Amount is required' : undefined}
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid>
                                    <Typography>
                                        <strong>Date Due</strong>
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <DatePicker
                                        id='due-date-picker'
                                        selectedDate={paymentDate}
                                        onChange={handlePaymentDate}
                                        minDate={new Date(Date.now())}
                                    />
                                    {showErrors && !paymentDate && (
                                        <FormHelperText error>Date due is required</FormHelperText>
                                    )}
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <>
                            {/* Extra Services flow: bill-based fields */}
                            <Grid item xs={12}>
                                <Grid>
                                    <Typography>
                                        <strong>Date Received</strong>
                                    </Typography>
                                    <Typography variant='body2' style={{ opacity: 0.6 }}>
                                        When you received the bill
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <DatePicker
                                        id='date-received-picker'
                                        selectedDate={dateReceived}
                                        onChange={handleDateReceived}
                                        maxDate={new Date()}
                                    />
                                    {showErrors && !dateReceived && (
                                        <FormHelperText error>Date received is required</FormHelperText>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid>
                                    <Typography>
                                        <strong>Date Due</strong>
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <DatePicker
                                        id='due-date-picker'
                                        selectedDate={paymentDate}
                                        onChange={handlePaymentDate}
                                        minDate={new Date(Date.now())}
                                    />
                                    {showErrors && !paymentDate && (
                                        <FormHelperText error>Date due is required</FormHelperText>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid>
                                    <Typography>
                                        <strong>Total Bill Amount</strong>
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <InputFieldMoney
                                        id='total-bill-amount'
                                        placeholder='Total bill amount (CAD)'
                                        fullWidth
                                        value={totalBillAmount}
                                        onChange={handleTotalBillAmountChange}
                                        error={showErrors && !totalBillAmount}
                                        helperText={showErrors && !totalBillAmount ? 'Total bill amount is required' : undefined}
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid>
                                    <Typography>
                                        <strong>Percentage Owing</strong>
                                    </Typography>
                                    <Typography variant='body2' style={{ opacity: 0.6 }}>
                                        Tenant's share of the total bill
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <TextField
                                        type='number'
                                        variant='outlined'
                                        fullWidth
                                        margin='normal'
                                        value={percentageOwing}
                                        onChange={handlePercentageOwingChange}
                                        inputProps={{ min: 0, max: 100 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>%</InputAdornment>
                                            ),
                                        }}
                                        error={showErrors && !percentageOwing}
                                        helperText={showErrors && !percentageOwing ? 'Percentage owing is required' : undefined}
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid>
                                    <Typography>
                                        <strong>Amount to Request</strong>
                                    </Typography>
                                    <Typography variant='body2' style={{ opacity: 0.6 }}>
                                        Calculated from ${totalBillAmount.toFixed(2)} × {percentageOwing}%
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <InputFieldMoney
                                        id='computed-amount'
                                        placeholder='Amount to request (CAD)'
                                        fullWidth
                                        value={computedAmount}
                                        readOnly
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} container spacing={1}>
                        <Grid item xs={12}>
                            <Typography>
                                <strong>Attachments</strong>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='subtitle1'>
                                <small>
                                    {isDamageFlow
                                        ? '(Recommended) Add photos, repair quotes, or invoices as evidence. Accepted formats: PNG, JPG or PDF.'
                                        : '(Optional) Add any relevant receipts or documentation. Accepted formats: PNG, JPG or PDF.'}
                                </small>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <DropzoneContainer
                                {...getRootProps({ isDragActive, isDragAccept, isDragReject })}
                            >
                                <input {...getInputProps()} />
                                {isDragActive ? (
                                    <p>Drop the files here ...</p>
                                ) : (
                                    <>
                                        <Button variant='outlined' startIcon={<PublishIcon />}>
                                            Upload files
                                        </Button>
                                        <Typography>or drag them in</Typography>
                                    </>
                                )}
                            </DropzoneContainer>
                        </Grid>
                        <Grid item xs={12} container spacing={2}>
                            {imageThumbs}
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid>
                            <Typography>
                                <strong>Notes</strong>
                            </Typography>
                        </Grid>
                        <Grid>
                            <Typography variant='subtitle1'>
                                <small> Explain why you are requesting the money </small>
                            </Typography>
                        </Grid>
                        <Grid>
                            <TextField
                                value={requestNote}
                                variant='outlined'
                                multiline
                                fullWidth
                                minRows={5}
                                onChange={handleRequestNoteChange}
                                error={showErrors && !requestNote}
                                helperText={showErrors && !requestNote ? 'Notes are required' : undefined}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        );
    },
);
